import moment from 'moment'
import { EmptyMediaPropsFactory, IMediaProps } from './Media'
import { EmptyFilePropsFactory, IFileProps } from './File'
import {
  UserNames,
  UserProperties,
  UserFlags,
  UserJob,
  UserSupervisor,
  UserPayments,
  UserApplication,
  UserMaintenance,
  UserCertificate,
  UserName,
  RelationHospital,
  RelationPharmaceutical,
  RelationDepartment,
  RelationIndustry
} from '@/infrastructure/network/Monitaro/schema/models'

export interface IUserProps {
  id?: string
  companyId?: string | null
  industryId?: string | null
  industry?: RelationIndustry | null
  hospitals?: RelationHospital[] | null
  pharmaceuticals?: RelationPharmaceutical[] | null
  departments?: RelationDepartment[] | null
  username: string | null
  login: string | null
  fcmToken: string | null
  uuid: string | null
  fullname: string | null
  kana: string | null
  organization: string | null
  icon: string | null
  approval: string | null
  name: UserNames | null
  properties: UserProperties | null
  flags: UserFlags | null
  job: UserJob | null
  supervisor: UserSupervisor | null
  payments: UserPayments | null
  application: UserApplication | null
  maintenance: UserMaintenance | null
  certificate: UserCertificate | null
  status: string | null
  activation: boolean | null
  approve: string | null
  withdrawedAt: string | null
  lockedAt: string | null
  canceledAt: string | null
  stoppedAt: string | null
  createdAt: string | null
  updatedAt: string | null
  joinedAt: string | null
  submissionDoc?: IUserSubmissionDocProps | null
  files: IUserFilesProps
  data: IUserDataProps
  favorite: boolean | null
  note: string | null
  common: string | null
}

export interface IUserSubmissionDocProps {
  id?: string | null
  chargeId?: string | null
  mediaId?: string | null
  file?: IFileProps | null
  editable?: boolean | null
  submittedAt?: string | null
}

export interface IUserQueryProps {
  status: string[] | null
  industries: string[] | null
}

export interface IUserFilesProps {
  image: IFileProps | null
  card: IFileProps | null
}

export interface IUserDataProps {
  image: File | null
  card: File | null
}

export default class UserEntity {
  private _props: IUserProps

  constructor(props?: IUserProps | null) {
    this._props = EmptyUserPropsFactory(props)
  }

  get props(): IUserProps {
    return this._props
  }

  get clone(): IUserProps {
    return structuredClone(this._props)
  }

  get id(): string | undefined {
    return this._props.id
  }

  get industryId(): string | null | undefined {
    return this._props.industryId
  }

  get companyId(): string | null | undefined {
    return this._props.companyId
  }

  get hospitals(): RelationHospital[] | null | undefined {
    return this._props.hospitals
  }

  get pharmaceuticals(): RelationPharmaceutical[] | null | undefined {
    return this._props.pharmaceuticals
  }

  get departments(): RelationDepartment[] | null | undefined {
    return this._props.departments
  }

  get username(): string | null {
    return this._props.username
  }

  get login(): string | null {
    return this._props.login
  }

  get fcmToken(): string | null {
    return this._props.fcmToken
  }

  get uuid(): string | null {
    return this._props.uuid
  }

  get fullname(): string | null {
    return this._props.fullname
  }

  get kana(): string | null {
    return this._props.kana
  }

  get organization(): string | null {
    return this._props.organization
  }

  get icon(): string | null {
    return this._props.icon
  }

  get withdrawedAt(): string | null {
    return this._props.withdrawedAt
  }

  get lockedAt(): string | null {
    return this._props.lockedAt
  }

  get canceledAt(): string | null {
    return this._props.canceledAt
  }

  get stoppedAt(): string | null {
    return this._props.stoppedAt
  }

  get joinedAt(): string | null {
    return this._props.joinedAt ? moment(this._props.joinedAt).format('YYYY/MM/DD') : null
  }

  get createdAt(): string | null {
    return this._props.createdAt ? moment(this._props.createdAt).format('YYYY/MM/DD') : null
  }

  get updatedAt(): string | null {
    return this._props.updatedAt
  }

  get industry(): RelationIndustry | null | undefined {
    return this._props.industry
  }

  submissionDocFile(): IFileProps | null {
    const submissionDocFile = this._props.submissionDoc?.file
    return submissionDocFile?.path ? submissionDocFile : null
  }

  get name(): UserNames | null | undefined {
    return this._props.name
  }

  get properties(): UserProperties | null | undefined {
    return this._props.properties
  }

  get flags(): UserFlags | null | undefined {
    return this._props.flags
  }

  get job(): UserJob | null | undefined {
    return this._props.job
  }

  get supervisor(): UserSupervisor | null | undefined {
    return this._props.supervisor
  }

  get payments(): UserPayments | null | undefined {
    return this._props.payments
  }

  get application(): UserApplication | null | undefined {
    return this._props.application
  }

  get maintenance(): UserMaintenance | null | undefined {
    return this._props.maintenance
  }

  get certificate(): UserCertificate | null | undefined {
    return this._props.certificate
  }

  get files(): IUserFilesProps {
    return this._props.files
  }

  get phone(): string | null | undefined {
    return this._props.properties?.phone
  }

  get customer(): string | null | undefined {
    return this._props.job?.customer
  }

  get academic(): string | null | undefined {
    return this._props.properties?.academic
  }

  get image(): IMediaProps | null | undefined {
    return EmptyMediaPropsFactory()
  }

  get card(): IMediaProps | null | undefined {
    return EmptyMediaPropsFactory()
  }

  get mr(): string | null | undefined {
    return this._props.properties?.mr
  }

  get favorite(): boolean | null | undefined {
    return this._props.favorite
  }

  get approve(): string | null | undefined {
    return this._props.approve
  }

  get note(): string | null {
    return this._props.note
  }

  get common(): string | null {
    return this._props.common
  }

  get approval(): string | null {
    return approval.find((item) => item.value === this._props.approval)?.text || '許可待ち'
  }
}

export const headers = [
  { text: '', value: 'favorite', class: 'min' },
  { text: '', value: 'avatar', class: 'min' },
  { text: '名前', value: 'user', sortable: true },
  { text: '企業/担当領域', value: 'organization', sortable: true },
  { text: '連絡先', value: 'contact' },
  { text: '登録日', value: 'joinedAt', sortable: true }
]

export const approved = [
  { text: '', value: 'favorite' },
  { text: '', value: 'avatar' },
  { text: '企業/担当領域', value: 'organization', sortable: true },
  { text: '名前', value: 'user', sortable: true },
  { text: '連絡先', value: 'contact' },
  { text: '登録日', value: 'joinedAt', sortable: true },
  { text: '承認', value: 'approval', sortable: true }
]

export const messages = [
  { text: '', value: 'favorite' },
  { text: '', value: 'avatar' },
  { text: '企業/担当領域', value: 'organization' },
  { text: '名前', value: 'user' },
  { text: '登録日', value: 'joinedAt' }
]

export const approval = [
  { value: 'approved', text: '許可' },
  { value: 'reject', text: '不許可' },
  { value: 'review', text: '許可待ち' }
]

export const users = [{ text: '名前', value: 'user' }]

export const EmptyUserPropsFactory = (props?: Partial<IUserProps> | null): IUserProps => ({
  username: null,
  login: null,
  fcmToken: null,
  uuid: null,
  fullname: null,
  kana: null,
  organization: null,
  icon: null,
  approval: null,
  name: null,
  flags: null,
  job: null,
  payments: null,
  application: null,
  maintenance: null,
  certificate: null,
  status: null,
  activation: null,
  approve: null,
  withdrawedAt: null,
  lockedAt: null,
  canceledAt: null,
  stoppedAt: null,
  joinedAt: null,
  submissionDoc: null,
  createdAt: null,
  updatedAt: null,
  favorite: false,
  note: null,
  common: null,
  ...props,
  properties: EmptyUserPropertiesFactory(props?.properties),
  supervisor: EmptyUserSupervisorFactory(props?.supervisor),
  files: EmptyUserFilesPropsFactory(props?.files),
  data: EmptyUserDataPropsFactory(props?.data)
})

export const EmptyUserQueryPropsFactory = (props?: Partial<IUserQueryProps> | null): IUserQueryProps => ({
  status: null,
  industries: null,
  ...props
})

export const EmptyUserNamesFactory = (props?: Partial<UserNames> | null): UserNames => ({
  primary: EmptyUserNameFactory(props?.primary),
  kana: EmptyUserNameFactory(props?.kana),
  ...props
})

export const EmptyUserNameFactory = (props?: Partial<UserName> | null): UserName => ({
  family: null,
  given: null,
  ...props
})

export const EmptyUserPropertiesFactory = (props?: Partial<UserProperties> | null): UserProperties => ({
  identifier: null,
  activation: null,
  phone: null,
  gender: null,
  birthday: null,
  experience: null,
  academic: null,
  mr: null,
  ...props
})

export const EmptyUserFlagsFactory = (props?: Partial<UserFlags> | null): UserFlags => ({
  locked: false,
  identification: false,
  chargeable: false,
  chat: false,
  valid_email: false,
  withdraw: false,
  ...props
})

export const EmptyUserJobFactory = (props?: Partial<UserJob> | null): UserJob => ({
  zipcode: null,
  prefecture: null,
  city: null,
  building: null,
  phone: null,
  customer: null,
  division: null,
  branch: null,
  position: null,
  ...props
})

export const EmptyUserSupervisorFactory = (props?: Partial<UserSupervisor> | null): UserSupervisor => ({
  phone: null,
  email: null,
  notification: null,
  ...props,
  name: EmptyUserNamesFactory(props?.name)
})

export const EmptyUserPaymentsFactory = (props?: Partial<UserPayments> | null): UserPayments => ({
  type: null,
  card: null,
  masked: null,
  ...props
})

export const EmptyUserApplicationFactory = (props?: Partial<UserApplication> | null): UserApplication => ({
  os: null,
  version: null,
  ...props
})

export const EmptyUserMaintenanceFactory = (props?: Partial<UserMaintenance> | null): UserMaintenance => ({
  approval: null,
  created: null,
  ...props
})

export const EmptyUserCertificateFactory = (props?: Partial<UserCertificate> | null): UserCertificate => ({
  pcr: null,
  covid19: null,
  vaccine: null,
  ...props
})

export const EmptyUserDataPropsFactory = (props?: Partial<IUserDataProps> | null): IUserDataProps => ({
  image: null,
  card: null,
  ...props
})

export const EmptyUserFilesPropsFactory = (props?: Partial<IUserFilesProps> | null): IUserFilesProps => ({
  image: EmptyFilePropsFactory(),
  card: EmptyFilePropsFactory(),
  ...props
})
